import type {
  CrudAddPayload,
  CrudDeletePayload,
  CrudEditPayload,
  CrudLoadAllPayload,
  CrudLoadByIdPayload,
} from '@indutrust/shared-model';
import { type } from '@ngrx/signals';
import type { EntityId } from '@ngrx/signals/entities';
import { eventGroup } from '@ngrx/signals/events';

/**
 * Factory function to create a standardized event group for CRUD operations.
 *
 * Generates events for loadAll, loadById, add, edit, and delete operations,
 * each with success and failure variants.
 *
 * @template Entity The entity type for this CRUD event group.
 * @param source The event source namespace (e.g., 'shared.country', 'shared.project').
 *               Defaults to 'shared.crud' if not provided.
 * @returns An event group with CRUD lifecycle events.
 * @example
 * ```typescript
 * // With custom source
 * export const countryEvents = createCrudEvents<Country>('shared.country');
 *
 * // With default source
 * export const projectEvents = createCrudEvents<Project>();
 * ```
 */
export function createCrudEvents<Entity extends { id: EntityId }>(
  source = 'shared'
) {
  return eventGroup({
    source: source + '.crud',
    events: {
      // LoadAll events
      loadAllRequested: type<CrudLoadAllPayload>(),
      loadAllSucceeded: type<Entity[]>(),
      loadAllFailed: type<string>(),

      // LoadById events
      loadByIdRequested: type<CrudLoadByIdPayload>(),
      loadByIdSucceeded: type<Entity>(),
      loadByIdFailed: type<string>(),

      // Add events
      addRequested: type<CrudAddPayload<Entity>>(),
      addSucceeded: type<Entity>(),
      addFailed: type<string>(),

      // Edit events
      editRequested: type<CrudEditPayload<Entity>>(),
      editSucceeded: type<Entity>(),
      editFailed: type<string>(),

      // Delete events
      deleteRequested: type<CrudDeletePayload>(),
      deleteSucceeded: type<EntityId>(),
      deleteFailed: type<string>(),

      // Selection
      selectedEntityChanged: type<EntityId | null>(),
    },
  });
}
